/**
 * Theme-aware color field utilities for component editors.
 *
 * These utilities help editors display accurate theme/brand package color defaults
 * instead of hardcoded values, ensuring the color picker preview matches what
 * will actually render on the live site.
 */

export interface ColorFieldConfig {
  key: string;
  label: string;
  defaultValue: string;
  placeholder: string;
  hint?: string;
  /** When true, text field accepts hex or rgba()/rgb() (e.g. semi-transparent overlay). */
  allowCssColor?: boolean;
}

/**
 * Neutral fallback colors used only when theme has no value.
 * These are intentionally neutral (not theme-specific like Bold's red).
 */
const NEUTRAL_FALLBACKS: Record<string, string> = {
  // Primary/accent colors
  primary: '#3b82f6', // Blue-500
  buttonBg: '#3b82f6',
  buttonBackground: '#3b82f6',
  accentColor: '#3b82f6',
  accent: '#3b82f6',
  linkHover: '#3b82f6',
  priceColor: '#3b82f6',
  iconColor: '#3b82f6',
  icon: '#3b82f6',
  starColor: '#fbbf24', // Amber for stars

  // Backgrounds
  background: '#ffffff',
  cardBackground: '#ffffff',
  cardBg: '#ffffff',
  inputBackground: '#ffffff',
  itemBackground: '#ffffff',
  overlayColor: '#1f2937',

  // Text colors
  titleColor: '#111827',
  title: '#111827',
  text: '#111827',
  subtitle: '#6B7280',
  subtitleColor: '#6B7280',
  textColor: '#4B5563',
  cardText: '#4B5563',
  mutedText: '#9CA3AF',
  description: '#e5e7eb',
  questionText: '#111827',
  answerText: '#4b5563',
  quoteText: '#374151',
  nameText: '#111827',
  nameColor: '#111827',
  roleColor: '#3b82f6',
  headline: '#111827',
  subheadline: '#6b7280',
  number: '#3b82f6',
  label: '#6b7280',

  // Links
  linkText: '#ffffff',
  brandText: '#111827',

  // Button text
  buttonText: '#ffffff',
  featureColor: '#4B5563',

  // Borders
  inputBorder: '#d1d5db',
  borderColor: '#e5e7eb',
  border: '#e5e7eb',
  caption: '#4b5563',

  // Conversion popup (avoid falling back to primary blue when theme key is missing)
  overlay: 'rgba(0,0,0,0.55)',
  panelBackground: '#ffffff',
  body: '#374151',
  closeButton: '#6b7280',
};

/**
 * Get a color value from theme colors using dot notation path.
 * Example: getNestedValue(themeColors, 'video.buttonBg')
 */
function getNestedValue(obj: Record<string, any> | null, path: string): string | undefined {
  if (!obj) return undefined;
  const parts = path.split('.');
  let current: any = obj;
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    current = current[part];
  }
  return typeof current === 'string' ? current : undefined;
}

/**
 * Mapping of component types to their theme color paths.
 * Each key in the inner object maps to a color field key used in the editor,
 * and the value is the path to look up in themeColors.
 */
const COMPONENT_COLOR_MAPPINGS: Record<string, Record<string, string>> = {
  hero: {
    background: 'hero.background',
    text: 'hero.text',
    buttonBg: 'hero.buttonBg',
    buttonText: 'hero.buttonText',
  },
  'split-hero': {
    background: 'splitHero.background',
    tagline: 'splitHero.taglineColor',
    title: 'splitHero.titleColor',
    subtitle: 'splitHero.subtitleColor',
    buttonBg: 'splitHero.buttonBg',
    buttonText: 'splitHero.buttonText',
  },
  'html-split': {
    background: 'htmlSplit.background',
    tagline: 'htmlSplit.taglineColor',
    title: 'htmlSplit.titleColor',
    subtitle: 'htmlSplit.subtitleColor',
    buttonBg: 'htmlSplit.buttonBg',
    buttonText: 'htmlSplit.buttonText',
  },
  cta: {
    background: 'cta.background',
    text: 'cta.text',
    buttonBackground: 'cta.buttonBg',
    buttonText: 'cta.buttonText',
  },
  video: {
    background: 'video.background',
    title: 'video.titleColor',
    subtitle: 'video.subtitleColor',
    text: 'video.textColor',
    buttonBackground: 'video.buttonBg',
    buttonText: 'video.buttonText',
  },
  'media-text': {
    background: 'mediaText.background',
    title: 'mediaText.titleColor',
    subtitle: 'mediaText.subtitleColor',
    text: 'mediaText.textColor',
    buttonBackground: 'mediaText.buttonBg',
    buttonText: 'mediaText.buttonText',
  },
  form: {
    background: 'form.background',
    title: 'form.labelColor',
    inputBackground: 'form.inputBg',
    inputBorder: 'form.inputBorder',
    buttonBackground: 'form.buttonBg',
    buttonText: 'form.buttonText',
  },
  'form-split': {
    background: 'form.background',
    title: 'form.labelColor',
    inputBackground: 'form.inputBg',
    inputBorder: 'form.inputBorder',
    buttonBackground: 'form.buttonBg',
    buttonText: 'form.buttonText',
  },
  footer: {
    background: 'footer.background',
    text: 'footer.text',
    mutedText: 'footer.mutedText',
    linkHover: 'footer.linkHover',
  },
  pricing: {
    background: 'pricing.background',
    text: 'pricing.titleColor',
    cardBackground: 'pricing.cardBg',
    cardText: 'pricing.featureColor',
    priceColor: 'pricing.priceColor',
    featureColor: 'pricing.featureColor',
    buttonBg: 'pricing.buttonBg',
    buttonText: 'pricing.buttonText',
  },
  'image-card': {
    background: 'imageCard.background',
    overlayColor: 'imageCard.overlayColor',
    title: 'imageCard.title',
    description: 'imageCard.description',
    buttonBg: 'imageCard.buttonBg',
    buttonText: 'imageCard.buttonText',
  },
  'image-gallery': {
    background: 'imageGallery.background',
    title: 'imageGallery.title',
    subtitle: 'imageGallery.subtitle',
    caption: 'imageGallery.caption',
    buttonBg: 'imageGallery.buttonBg',
    buttonText: 'imageGallery.buttonText',
  },
  'image-info': {
    background: 'imageInfo.background',
    title: 'imageInfo.title',
    subtitle: 'imageInfo.subtitle',
    text: 'imageInfo.text',
    buttonBg: 'imageInfo.buttonBg',
    buttonText: 'imageInfo.buttonText',
  },
  navbar: {
    background: 'navbar.background',
    text: 'navbar.text',
    brandText: 'navbar.brandText',
    linkText: 'navbar.linkText',
    linkHover: 'navbar.linkHover',
  },
  'navigation-menu': {
    background: 'navigationMenu.background',
    text: 'navigationMenu.text',
    brandText: 'navigationMenu.brandText',
    linkText: 'navigationMenu.linkText',
    linkHover: 'navigationMenu.linkHover',
    dropdownBackground: 'navigationMenu.dropdownBackground',
    dropdownText: 'navigationMenu.dropdownText',
    dropdownHover: 'navigationMenu.dropdownHover',
  },
  features: {
    background: 'features.background',
    cardBg: 'features.cardBg',
    iconColor: 'features.iconColor',
    title: 'features.titleColor',
    text: 'features.textColor',
    buttonBg: 'features.buttonBg',
    buttonText: 'features.buttonText',
  },
  faq: {
    background: 'faq.background',
    title: 'faq.titleColor',
    subtitle: 'faq.subtitleColor',
    questionText: 'faq.questionColor',
    answerText: 'faq.answerColor',
    itemBackground: 'faq.itemBackground',
    iconColor: 'faq.iconColor',
    buttonBg: 'faq.buttonBg',
    buttonText: 'faq.buttonText',
  },
  testimonials: {
    background: 'testimonials.background',
    title: 'testimonials.titleColor',
    subtitle: 'testimonials.subtitleColor',
    cardBackground: 'testimonials.cardBackground',
    quoteText: 'testimonials.quoteColor',
    nameText: 'testimonials.nameColor',
    starColor: 'testimonials.starColor',
    buttonBg: 'testimonials.buttonBg',
    buttonText: 'testimonials.buttonText',
  },
  team: {
    background: 'team.background',
    text: 'team.nameColor',
    cardBackground: 'team.cardBg',
    cardText: 'team.roleColor',
    nameColor: 'team.nameColor',
    roleColor: 'team.roleColor',
  },
  'contact-info': {
    background: 'contactInfo.background',
    title: 'contactInfo.titleColor',
    text: 'contactInfo.textColor',
    accent: 'contactInfo.accentColor',
    buttonBg: 'contactInfo.buttonBg',
    buttonText: 'contactInfo.buttonText',
  },
  'text-blocks': {
    background: 'textBlocks.background',
    title: 'textBlocks.titleColor',
    subtitle: 'textBlocks.subtitleColor',
    text: 'textBlocks.textColor',
    border: 'textBlocks.linkColor',
    buttonBg: 'textBlocks.buttonBg',
    buttonText: 'textBlocks.buttonText',
  },
  'title-subtitle': {
    background: 'titleSubtitle.background',
    title: 'titleSubtitle.titleColor',
    subtitle: 'titleSubtitle.subtitleColor',
    buttonBg: 'titleSubtitle.buttonBg',
    buttonText: 'titleSubtitle.buttonText',
  },
  'simple-header': {
    background: 'simpleHeader.background',
    title: 'simpleHeader.titleColor',
    subtitle: 'simpleHeader.subtitleColor',
    buttonBg: 'simpleHeader.buttonBg',
    buttonText: 'simpleHeader.buttonText',
  },
  'countdown-timer': {
    background: 'countdownTimer.background',
    text: 'countdownTimer.text',
    headline: 'countdownTimer.headline',
    subheadline: 'countdownTimer.subheadline',
    number: 'countdownTimer.number',
    label: 'countdownTimer.label',
    buttonBg: 'countdownTimer.buttonBg',
    buttonText: 'countdownTimer.buttonText',
  },
  'promo-banner': {
    background: 'promoBanner.background',
    text: 'promoBanner.text',
    linkText: 'promoBanner.linkText',
  },
  logocloud: {
    background: 'logocloud.background',
    title: 'logocloud.titleColor',
    subtitle: 'logocloud.subtitleColor',
  },
  stats: {
    background: 'stats.background',
    title: 'stats.titleColor',
    subtitle: 'stats.subtitleColor',
    value: 'stats.valueColor',
    label: 'stats.labelColor',
    description: 'stats.descriptionColor',
  },
  'blog-preview': {
    background: 'blogPreview.background',
    title: 'blogPreview.titleColor',
    subtitle: 'blogPreview.subtitleColor',
    cardBackground: 'blogPreview.cardBackground',
    cardTitle: 'blogPreview.cardTitle',
    cardExcerpt: 'blogPreview.cardExcerpt',
    linkColor: 'blogPreview.linkColor',
  },
  'conversion-popup': {
    overlay: 'conversionPopup.overlay',
    panelBackground: 'conversionPopup.panelBackground',
    headline: 'conversionPopup.headlineColor',
    body: 'conversionPopup.bodyTextColor',
    buttonBackground: 'conversionPopup.buttonBackground',
    buttonText: 'conversionPopup.buttonText',
    closeButton: 'conversionPopup.closeButton',
  },
};

/**
 * Get the effective default color for a specific field.
 * Looks up theme colors first, then falls back to neutral defaults.
 */
export function getThemeColorDefault(
  componentType: string,
  fieldKey: string,
  themeColors: Record<string, any> | null
): string {
  // Try to get from theme colors using the mapping
  const mapping = COMPONENT_COLOR_MAPPINGS[componentType];
  if (mapping && mapping[fieldKey]) {
    const themeValue = getNestedValue(themeColors, mapping[fieldKey]);
    if (themeValue) return themeValue;
  }

  // Fall back to neutral defaults, then accent primary for uncommon field keys
  return NEUTRAL_FALLBACKS[fieldKey] || NEUTRAL_FALLBACKS.primary;
}

/**
 * Build color fields array with theme-aware defaults.
 * This replaces the static colorFields arrays in editors.
 */
export function buildColorFields(
  componentType: string,
  fieldConfigs: Array<{ key: string; label: string; hint?: string; allowCssColor?: boolean }>,
  themeColors: Record<string, any> | null
): ColorFieldConfig[] {
  return fieldConfigs.map(({ key, label, hint, allowCssColor }) => {
    const defaultValue = getThemeColorDefault(componentType, key, themeColors);
    return {
      key,
      label,
      defaultValue,
      placeholder: defaultValue,
      hint,
      allowCssColor,
    };
  });
}

/**
 * Standard color field definitions by component type.
 * These define which fields each component type supports.
 */
export const COMPONENT_COLOR_FIELDS: Record<
  string,
  Array<{ key: string; label: string; hint?: string; allowCssColor?: boolean }>
> = {
  hero: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'split-hero': [
    { key: 'background', label: 'Background Color' },
    { key: 'tagline', label: 'Tagline Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'html-split': [
    { key: 'background', label: 'Background Color' },
    { key: 'tagline', label: 'Tagline Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  cta: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'buttonBackground', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  video: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'text', label: 'Description Color' },
    { key: 'buttonBackground', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'media-text': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'text', label: 'Content Text Color' },
    { key: 'buttonBackground', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  form: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'inputBackground', label: 'Input Background' },
    { key: 'inputBorder', label: 'Input Border Color' },
    { key: 'buttonBackground', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'form-split': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'inputBackground', label: 'Input Background' },
    { key: 'inputBorder', label: 'Input Border Color' },
    { key: 'buttonBackground', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  footer: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'mutedText', label: 'Muted Text Color' },
    { key: 'linkHover', label: 'Link Hover Color' },
  ],
  pricing: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Title/Text Color' },
    { key: 'cardBackground', label: 'Card Background' },
    { key: 'cardText', label: 'Card Text Color' },
    { key: 'priceColor', label: 'Price Color' },
    { key: 'featureColor', label: 'Feature Text Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'image-card': [
    { key: 'background', label: 'Section Background' },
    { key: 'overlayColor', label: 'Card Overlay Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'description', label: 'Description Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text' },
  ],
  'image-gallery': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'caption', label: 'Caption Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'image-info': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  navbar: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    {
      key: 'brandText',
      label: 'Brand Text Color',
      hint: 'Overrides logo text color for this navbar',
    },
    { key: 'linkText', label: 'Link Text Color' },
    { key: 'linkHover', label: 'Link Hover Color' },
  ],
  'navigation-menu': [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    {
      key: 'brandText',
      label: 'Brand Text Color',
      hint: 'Overrides logo text color for this menu',
    },
    { key: 'linkText', label: 'Link Text Color' },
    { key: 'linkHover', label: 'Link Hover Color' },
    { key: 'dropdownBackground', label: 'Dropdown Background' },
    { key: 'dropdownText', label: 'Dropdown Text Color' },
    { key: 'dropdownHover', label: 'Dropdown Hover Color' },
  ],
  features: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'iconColor', label: 'Icon Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  faq: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'questionText', label: 'Question Color' },
    { key: 'answerText', label: 'Answer Color' },
    { key: 'itemBackground', label: 'Item Background' },
    { key: 'iconColor', label: 'Icon Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  testimonials: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'cardBackground', label: 'Card Background' },
    { key: 'quoteText', label: 'Quote Text Color' },
    { key: 'nameText', label: 'Name Color' },
    { key: 'starColor', label: 'Star Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  team: [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Section Text Color' },
    { key: 'cardBackground', label: 'Card Background' },
    { key: 'cardText', label: 'Card Text Color' },
    { key: 'nameColor', label: 'Name Color' },
    { key: 'roleColor', label: 'Role/Position Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'contact-info': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'accent', label: 'Accent Color (Icons)' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'text-blocks': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'text', label: 'Content Text Color' },
    { key: 'border', label: 'Border Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'title-subtitle': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'buttonBg', label: 'Button Background Color' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'simple-header': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'countdown-timer': [
    { key: 'background', label: 'Background Color' },
    { key: 'headline', label: 'Headline Color' },
    { key: 'subheadline', label: 'Subheadline Color' },
    { key: 'number', label: 'Number Color' },
    { key: 'label', label: 'Label Color' },
    { key: 'buttonBg', label: 'Button Background' },
    { key: 'buttonText', label: 'Button Text Color' },
  ],
  'promo-banner': [
    { key: 'background', label: 'Background Color' },
    { key: 'text', label: 'Text Color' },
    { key: 'linkText', label: 'Link Text Color' },
  ],
  logocloud: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
  ],
  stats: [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'value', label: 'Number Color' },
    { key: 'label', label: 'Label Color' },
    { key: 'description', label: 'Description Color' },
  ],
  'blog-preview': [
    { key: 'background', label: 'Background Color' },
    { key: 'title', label: 'Title Color' },
    { key: 'subtitle', label: 'Subtitle Color' },
    { key: 'cardBackground', label: 'Card Background' },
    { key: 'cardTitle', label: 'Card Title Color' },
    { key: 'cardExcerpt', label: 'Card Excerpt Color' },
    { key: 'linkColor', label: 'Link Color' },
  ],
  'conversion-popup': [
    {
      key: 'overlay',
      label: 'Overlay',
      allowCssColor: true,
      hint: 'Hex or rgba(), e.g. rgba(0,0,0,0.55). Clear with “Use theme default” to match your theme.',
    },
    { key: 'panelBackground', label: 'Panel background' },
    { key: 'headline', label: 'Headline color' },
    { key: 'body', label: 'Body text color' },
    { key: 'buttonBackground', label: 'Button background' },
    { key: 'buttonText', label: 'Button text' },
    { key: 'closeButton', label: 'Close button' },
  ],
};

/**
 * Get color fields for a component type with theme-aware defaults.
 * This is the main function editors should use.
 */
export function getColorFieldsForComponent(
  componentType: string,
  themeColors: Record<string, any> | null
): ColorFieldConfig[] {
  const fieldConfigs = COMPONENT_COLOR_FIELDS[componentType];
  if (!fieldConfigs) {
    console.warn(`No color field config found for component type: ${componentType}`);
    return [];
  }
  return buildColorFields(componentType, fieldConfigs, themeColors);
}
